import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Eye, EyeSlash } from '@phosphor-icons/react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const MEDIA = {
  hero: 'https://images.unsplash.com/photo-1580852300654-03c803a14e24?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1920',
};

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  
  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  const from = location.state?.from?.pathname || '/hub';

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/hub', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Please enter your email and password');
      return;
    }
    
    setLoading(true);
    const result = await login(email, password);
    setLoading(false);
    
    if (result.success) {
      toast.success('Welcome back, Brother');
      navigate('/hub', { replace: true });
    } else {
      toast.error(result.error);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-[#F4F0E6] flex">
      {/* Left Side - Image */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${MEDIA.hero})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#050505]" />
        <div className="absolute inset-0 bg-[#050505]/40" />
        
        {/* Overlay Content */}
        <div className="absolute bottom-0 left-0 right-0 p-12">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#C4A661] mb-4">
            The Brotherhood Awaits
          </p>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight leading-tight">
            Walk in Power.
            <br />
            <span className="font-editorial text-white/80">Build Your Legacy.</span>
          </h2>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center px-6 py-12">
        <motion.div 
          className="w-full max-w-md"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          {/* Logo */}
          <Link to="/" className="block mb-12">
            <h1 className="text-3xl font-bold tracking-tighter text-[#F4F0E6]">POWR</h1>
          </Link>

          {/* Form Header */}
          <div className="mb-10">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-3">
              Welcome Back
            </h2>
            <p className="text-white/50">
              Sign in to continue your journey
            </p>
          </div>

          {/* Login Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-xs font-bold uppercase tracking-[0.15em] text-white/50 mb-3">
                Email Address
              </label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="bg-white/5 border-white/10 text-[#F4F0E6] placeholder:text-white/30 h-14 px-4 rounded-none focus:border-[#C4A661] focus:ring-[#C4A661]"
                data-testid="email-input"
              />
            </div>

            <div>
              <label className="block text-xs font-bold uppercase tracking-[0.15em] text-white/50 mb-3">
                Password
              </label>
              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="bg-white/5 border-white/10 text-[#F4F0E6] placeholder:text-white/30 h-14 px-4 pr-12 rounded-none focus:border-[#C4A661] focus:ring-[#C4A661]"
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/70 transition-colors"
                >
                  {showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#C4A661] text-[#050505] font-bold h-14 rounded-none hover:bg-[#D4B671] transition-all disabled:opacity-50"
              data-testid="login-button"
            >
              {loading ? 'Signing in...' : 'Sign In'}
              <ArrowRight size={18} className="ml-2" />
            </Button>
          </form>

          {/* Divider */}
          <div className="my-10 flex items-center gap-4">
            <div className="flex-1 h-px bg-white/10" />
            <span className="text-xs uppercase tracking-widest text-white/30">or</span>
            <div className="flex-1 h-px bg-white/10" />
          </div>

          {/* Sign Up Link */}
          <div className="text-center">
            <p className="text-white/50 mb-4">
              New to POWR?
            </p>
            <Link to="/signup">
              <Button
                variant="outline"
                className="w-full border-white/20 text-[#F4F0E6] hover:border-[#C4A661] hover:text-[#C4A661] h-14 rounded-none transition-all"
                data-testid="signup-link"
              >
                Create Your Account
              </Button>
            </Link>
          </div>

          {/* Back to Home */}
          <div className="mt-10 text-center">
            <Link to="/" className="text-xs uppercase tracking-widest text-white/30 hover:text-white/60 transition-colors">
              ← Back to Home
            </Link>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default LoginPage;
